import { useState, useEffect, useCallback } from 'react';
import { getFeatureStats, getFeatureTrends, getUserSegments, getFeatureAdoption } from '../api/featureAnalytics';
import type {
  FeatureStats,
  FeatureTrends,
  UserSegments,
  FeatureAdoption,
  StatsPeriod,
  TrendsPeriod,
  SegmentData,
  AdoptionMetric,
} from '../types/featureAnalytics';
import styles from './FeatureAnalytics.module.css';

const FILE_TYPE_COLORS: Record<string, string> = {
  text: '#3b82f6',
  wiki: '#22c55e',
  grid: '#f59e0b',
  cardstack: '#a855f7',
  outline: '#ec4899',
  manuscript: '#06b6d4',
};

function pctBar(pct: number, color?: string) {
  return (
    <div className={styles.pctBarTrack}>
      <div
        className={styles.pctBarFill}
        style={{ width: `${Math.min(pct, 100)}%`, background: color }}
      />
    </div>
  );
}

function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace('Pct ', '% ')
    .replace('Google Drive Backup', 'Google Drive')
    .replace('Tutorial Completed Any Task', 'Completed Tutorial Task');
}

function formatTrendDate(date: string, granularity: string): string {
  if (!date) return '-';
  const d = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(d.getTime())) return date;
  if (granularity === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function FeatureAnalytics() {
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('all');
  const [trendsPeriod, setTrendsPeriod] = useState<TrendsPeriod>('30d');

  const [stats, setStats] = useState<FeatureStats | null>(null);
  const [trends, setTrends] = useState<FeatureTrends | null>(null);
  const [segments, setSegments] = useState<UserSegments | null>(null);
  const [adoption, setAdoption] = useState<FeatureAdoption | null>(null);

  const [loading, setLoading] = useState({ stats: true, trends: true, segments: true, adoption: true });
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const loadStats = useCallback(async () => {
    setLoading(l => ({ ...l, stats: true }));
    setErrors(e => ({ ...e, stats: null }));
    try {
      setStats(await getFeatureStats(statsPeriod));
    } catch { setErrors(e => ({ ...e, stats: 'Failed to load stats' })); }
    finally { setLoading(l => ({ ...l, stats: false })); }
  }, [statsPeriod]);

  const loadTrends = useCallback(async () => {
    setLoading(l => ({ ...l, trends: true }));
    setErrors(e => ({ ...e, trends: null }));
    try {
      setTrends(await getFeatureTrends(trendsPeriod));
    } catch { setErrors(e => ({ ...e, trends: 'Failed to load trends' })); }
    finally { setLoading(l => ({ ...l, trends: false })); }
  }, [trendsPeriod]);

  const loadSegments = useCallback(async () => {
    setLoading(l => ({ ...l, segments: true }));
    setErrors(e => ({ ...e, segments: null }));
    try {
      setSegments(await getUserSegments());
    } catch { setErrors(e => ({ ...e, segments: 'Failed to load segments' })); }
    finally { setLoading(l => ({ ...l, segments: false })); }
  }, []);

  const loadAdoption = useCallback(async () => {
    setLoading(l => ({ ...l, adoption: true }));
    setErrors(e => ({ ...e, adoption: null }));
    try {
      setAdoption(await getFeatureAdoption());
    } catch { setErrors(e => ({ ...e, adoption: 'Failed to load adoption' })); }
    finally { setLoading(l => ({ ...l, adoption: false })); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTrends(); }, [loadTrends]);
  useEffect(() => { loadSegments(); }, [loadSegments]);
  useEffect(() => { loadAdoption(); }, [loadAdoption]);

  const handleRefresh = () => { loadStats(); loadTrends(); loadSegments(); loadAdoption(); };
  const anyLoading = Object.values(loading).some(Boolean);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Feature Analytics</h1>
          <p className={styles.subtitle}>Feature usage, adoption rates, and user engagement</p>
        </div>
        <button className={styles.refreshButton} onClick={handleRefresh} disabled={anyLoading}>
          <span className={`${styles.refreshIcon} ${anyLoading ? styles.spinning : ''}`}>↻</span>
          Refresh All
        </button>
      </div>

      {/* ===== FILE TYPE STATS ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>File Type Distribution</h2>
          <select
            className={styles.periodSelect}
            value={statsPeriod}
            onChange={e => setStatsPeriod(e.target.value as StatsPeriod)}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {errors.stats && <div className={styles.error}>{errors.stats}</div>}

        {loading.stats && !stats ? (
          <div className={styles.loadingSmall}>Loading...</div>
        ) : stats && (
          <>
            <div className={styles.statCards}>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Total Files</div>
                <div className={styles.statValue}>{stats.file_types.total_files.toLocaleString()}</div>
              </div>
              {stats.file_types.by_origin.map(o => (
                <div key={o.origin} className={styles.statCard}>
                  <div className={styles.statLabel}>{o.origin === 'web' ? 'Web Created' : 'Device Created'}</div>
                  <div className={styles.statValue}>{o.count.toLocaleString()}</div>
                </div>
              ))}
            </div>

            <div className={styles.breakdownGrid}>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>By File Type</h3>
                {stats.file_types.breakdown.map(ft => (
                  <div key={ft.type} className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>
                      <span className={styles.dot} style={{ background: FILE_TYPE_COLORS[ft.type] || '#6b7280' }} />
                      {formatLabel(ft.type)}
                    </span>
                    <span className={styles.breakdownCount}>{ft.count.toLocaleString()}</span>
                    <span className={styles.breakdownPct}>{ft.percentage.toFixed(1)}%</span>
                    {pctBar(ft.percentage, FILE_TYPE_COLORS[ft.type])}
                  </div>
                ))}
              </div>

              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Projects</h3>
                <div className={styles.miniStatGrid}>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Total</span>
                    <span className={styles.miniStatValue}>{stats.projects.total.toLocaleString()}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Avg Files / Project</span>
                    <span className={styles.miniStatValue}>{stats.projects.avg_files_per_project.toFixed(1)}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Pinned</span>
                    <span className={styles.miniStatValue}>{stats.projects.pinned.toLocaleString()}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>With Background</span>
                    <span className={styles.miniStatValue}>{stats.projects.with_background_image.toLocaleString()}</span>
                  </div>
                </div>
                <h4 className={styles.subTitle}>Size Distribution</h4>
                {Object.entries(stats.projects.size_distribution).map(([bucket, count]) => (
                  <div key={bucket} className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>{bucket} files</span>
                    <span className={styles.breakdownCount}>{count.toLocaleString()}</span>
                    {pctBar(stats.projects.total > 0 ? (count / stats.projects.total) * 100 : 0)}
                  </div>
                ))}
              </div>

              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Content Features</h3>
                <div className={styles.miniStatGrid}>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Favorited Files</span>
                    <span className={styles.miniStatValue}>{stats.content_features.favorited_files.toLocaleString()}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>With Description</span>
                    <span className={styles.miniStatValue}>{stats.content_features.files_with_description.toLocaleString()}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Folders</span>
                    <span className={styles.miniStatValue}>{stats.content_features.total_folders.toLocaleString()}</span>
                  </div>
                  <div className={styles.miniStat}>
                    <span className={styles.miniStatLabel}>Nested Folders</span>
                    <span className={styles.miniStatValue}>{stats.content_features.nested_folders.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ===== TRENDS ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Creation Trends</h2>
          <select
            className={styles.periodSelect}
            value={trendsPeriod}
            onChange={e => setTrendsPeriod(e.target.value as TrendsPeriod)}
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {errors.trends && <div className={styles.error}>{errors.trends}</div>}

        {loading.trends && !trends ? (
          <div className={styles.loadingSmall}>Loading...</div>
        ) : trends && trends.file_creation_trends.length > 0 && (
          <div className={styles.trendsContainer}>
            <div className={styles.trendTable}>
              <h3 className={styles.breakdownTitle}>File Creation by Type</h3>
              <div className={styles.trendTableScroll}>
                <table className={styles.miniTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Total</th>
                      {Object.keys(FILE_TYPE_COLORS).map(t => (
                        <th key={t}>
                          <span className={styles.dot} style={{ background: FILE_TYPE_COLORS[t] }} />
                          {formatLabel(t)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trends.file_creation_trends.map(row => (
                      <tr key={row.date}>
                        <td className={styles.trendDate}>{formatTrendDate(row.date, trends.granularity)}</td>
                        <td className={styles.trendTotal}>{row.total}</td>
                        <td>{row.text}</td>
                        <td>{row.wiki}</td>
                        <td>{row.grid}</td>
                        <td>{row.cardstack}</td>
                        <td>{row.outline}</td>
                        <td>{row.manuscript}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.trendCards}>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Origin Trends</h3>
                {(() => {
                  const grouped = trends.origin_trends.reduce<Record<string, Record<string, number>>>((acc, pt) => {
                    (acc[pt.date] ||= {})[pt.origin] = pt.count;
                    return acc;
                  }, {});
                  return Object.entries(grouped).map(([date, origins]) => (
                    <div key={date} className={styles.originTrendRow}>
                      <span className={styles.trendDate}>{formatTrendDate(date, trends.granularity)}</span>
                      <span className={styles.originWeb}>Web: {origins['web'] || 0}</span>
                      <span className={styles.originDevice}>Device: {origins['device'] || 0}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Project Creation</h3>
                {trends.project_creation_trends.map(pt => (
                  <div key={pt.date} className={styles.originTrendRow}>
                    <span className={styles.trendDate}>{formatTrendDate(pt.date, trends.granularity)}</span>
                    <span>{pt.count} projects</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {trends && trends.file_creation_trends.length === 0 && !loading.trends && (
          <p className={styles.emptyNotice}>No trend data for the selected period.</p>
        )}
      </section>

      {/* ===== FEATURE ADOPTION ===== */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Feature Adoption</h2>

        {errors.adoption && <div className={styles.error}>{errors.adoption}</div>}

        {loading.adoption && !adoption ? (
          <div className={styles.loadingSmall}>Loading...</div>
        ) : adoption && (
          <div className={styles.adoptionGrid}>
            {Object.entries(adoption.features).map(([category, metrics]) => (
              <div key={category} className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>{formatLabel(category)}</h3>
                {Object.entries(metrics as Record<string, AdoptionMetric>).map(([feature, m]) => (
                  <div key={feature} className={styles.adoptionRow}>
                    <span className={styles.adoptionLabel}>{formatLabel(feature)}</span>
                    <span className={styles.adoptionUsers}>{m.users.toLocaleString()} users</span>
                    <span className={styles.adoptionPct}>{m.pct.toFixed(1)}%</span>
                    {pctBar(m.pct)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ===== USER SEGMENTS ===== */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>User Engagement Segments</h2>

        {errors.segments && <div className={styles.error}>{errors.segments}</div>}

        {loading.segments && !segments ? (
          <div className={styles.loadingSmall}>Loading...</div>
        ) : segments && (
          <>
            <p className={styles.segmentSubtitle}>
              {segments.total_users.toLocaleString()} total users segmented by file count
            </p>
            <div className={styles.segmentGrid}>
              {(Object.entries(segments.segments) as [string, SegmentData][]).map(([key, seg]) => (
                <div key={key} className={styles.segmentCard}>
                  <div className={styles.segmentHeader}>
                    <span className={styles.segmentName}>{seg.label}</span>
                    <span className={styles.segmentCount}>{seg.user_count.toLocaleString()} users</span>
                  </div>
                  <div className={styles.segmentPctBar}>
                    {pctBar(seg.percentage_of_total)}
                    <span className={styles.segmentPctLabel}>{seg.percentage_of_total.toFixed(1)}% of users</span>
                  </div>
                  <div className={styles.segmentStats}>
                    <div><span className={styles.segStatLabel}>Avg Files</span> <span>{seg.avg_files.toFixed(1)}</span></div>
                    <div><span className={styles.segStatLabel}>Avg Projects</span> <span>{seg.avg_projects.toFixed(1)}</span></div>
                    <div><span className={styles.segStatLabel}>Using Tags</span> <span>{seg.pct_using_tags.toFixed(0)}%</span></div>
                    <div><span className={styles.segStatLabel}>Using Devices</span> <span>{seg.pct_using_devices.toFixed(0)}%</span></div>
                    <div><span className={styles.segStatLabel}>Google Drive</span> <span>{seg.pct_using_google_drive.toFixed(0)}%</span></div>
                    <div><span className={styles.segStatLabel}>Subscribers</span> <span>{seg.pct_active_subscribers.toFixed(0)}%</span></div>
                    <div><span className={styles.segStatLabel}>Tutorial Done</span> <span>{seg.pct_dismissed_tutorial.toFixed(0)}%</span></div>
                  </div>
                  {segments.file_types_by_segment[key]?.length > 0 && (
                    <div className={styles.segmentFileTypes}>
                      {segments.file_types_by_segment[key].map(ft => (
                        <span key={ft.type} className={styles.segFileType}>
                          <span className={styles.dot} style={{ background: FILE_TYPE_COLORS[ft.type] || '#6b7280' }} />
                          {ft.type}: {ft.count.toLocaleString()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
